"use client";

import { useState, useCallback } from "react";
import Link from "next/link";
import { Button } from "~/components/ui/button";

import { Checkbox } from "~/components/ui/checkbox";
import { OptionalEmailFields } from "~/components/ui/optional-email-fields";
import { QuestionChoice } from "~/components/ui/question-choice";
import { QuestionFlowShell } from "~/components/ui/question-flow-shell";
import { QuestionTextField } from "~/components/ui/question-text-field";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";

import { ArrowLeft, ArrowRight } from "lucide-react";
import { z } from "zod/v3";
import { type QuizQuestion } from "~/types/quiz";
import { cn } from "~/lib/utils";
import { useSession } from "next-auth/react";

interface Answer {
  questionIndex: number;
  answer: string | string[];
}

interface QuizProps {
  questions: QuizQuestion[];
  quizType: string;
  title: string;
  onSubmitSuccess: (data: {
    email?: string;
    answers: Answer[];
    newsletterOptIn: boolean;
  }) => Promise<void>;
}

const emailSchema = z.string().email("Unesi ispravnu email adresu");

export default function Quiz({
  questions,
  onSubmitSuccess,
  quizType,
  title,
}: QuizProps) {
  const { data: session, status } = useSession();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [email, setEmail] = useState(session?.user?.email ?? "");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [newsletterOptIn, setNewsletterOptIn] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const isOnEmailStep = currentQuestion >= questions.length;
  const progressStep = isOnEmailStep
    ? questions.length + 1
    : currentQuestion + 1;
  const progressSteps = questions.length + 1;

  const handleAnswer = (questionIndex: number, answer: string | string[]) => {
    setAnswers((prev) => {
      const existingIndex = prev.findIndex(
        (existingAnswer) => existingAnswer.questionIndex === questionIndex,
      );

      if (existingIndex !== -1) {
        const newAnswers = [...prev];
        newAnswers[existingIndex] = { questionIndex, answer };
        return newAnswers;
      } else {
        return [...prev, { questionIndex, answer }];
      }
    });
  };

  const submit = useCallback(
    async (data: {
      email?: string;
      answers: Answer[];
      newsletterOptIn: boolean;
    }) => {
      if (isSubmitting) return;

      setIsSubmitting(true);
      setSubmitError(null);
      try {
        await onSubmitSuccess(data);
      } catch {
        setSubmitError("Nismo uspeli da otvorimo rezultat. Pokušaj ponovo.");
        setIsSubmitting(false);
      }
    },
    [isSubmitting, onSubmitSuccess],
  );

  const handleNext = useCallback(() => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion((current) => current + 1);
    } else {
      if (session?.user?.email) {
        void submit({
          email: session.user.email,
          answers,
          newsletterOptIn,
        });
      } else {
        setCurrentQuestion(questions.length);
      }
    }
  }, [
    currentQuestion,
    questions.length,
    session?.user?.email,
    answers,
    newsletterOptIn,
    submit,
  ]);

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion((prev) => prev - 1);
    }
  };

  const handleSubmit = () => {
    const trimmedEmail = email.trim();

    // Email is optional: it only buys delivery of the result. A checked
    // newsletter opt-in needs an address to act on, so ask for one then.
    if (trimmedEmail.length === 0) {
      if (newsletterOptIn) {
        setEmailError("Unesi email da bismo ti slali savete i ponude.");
        return;
      }

      setEmailError(null);
      void submit({ answers, newsletterOptIn: false });
      return;
    }

    const result = emailSchema.safeParse(trimmedEmail);
    if (!result.success) {
      setEmailError(
        result.error.errors[0]?.message ?? "Nevalidna email adresa",
      );
      return;
    }

    setEmailError(null);
    void submit({ email: trimmedEmail, answers, newsletterOptIn });
  };

  const getTextAnswer = (questionIndex: number) => {
    const answer = answers.find(
      (item) => item.questionIndex === questionIndex,
    )?.answer;

    return typeof answer === "string" ? answer : "";
  };

  const renderQuestion = (question: QuizQuestion) => {
    switch (question.type) {
      case "checkbox":
        return (
          <div className="grid gap-3 sm:grid-cols-2">
            <p className="col-span-full text-sm text-muted-foreground">
              Možeš izabrati više odgovora
            </p>
            {question.options?.map((option) => (
              <QuestionChoice
                key={option}
                selected={(
                  answers.find((a) => a.questionIndex === currentQuestion)
                    ?.answer ?? []
                ).includes(option)}
                control={
                  <Checkbox
                    checked={(
                      answers.find((a) => a.questionIndex === currentQuestion)
                        ?.answer ?? []
                    ).includes(option)}
                    onCheckedChange={(checked) => {
                      const currentAnswers =
                        answers.find((a) => a.questionIndex === currentQuestion)
                          ?.answer ?? [];
                      if (checked) {
                        handleAnswer(currentQuestion, [
                          ...currentAnswers,
                          option,
                        ]);
                      } else {
                        handleAnswer(
                          currentQuestion,
                          (currentAnswers as string[]).filter(
                            (a) => a !== option,
                          ),
                        );
                      }
                    }}
                    className="shrink-0 border-input data-[state=checked]:border-brand data-[state=checked]:bg-brand-action data-[state=checked]:text-white"
                  />
                }
              >
                {option}
              </QuestionChoice>
            ))}
          </div>
        );
      case "radio":
        return (
          <div className="max-w-2xl">
            <RadioGroup
              value={
                (answers.find((a) => a.questionIndex === currentQuestion)
                  ?.answer as string | undefined) ?? ""
              }
              onValueChange={(value) => {
                handleAnswer(currentQuestion, value);
              }}
              className="grid gap-3"
            >
              {question.options?.map((option) => (
                <QuestionChoice
                  key={option}
                  selected={
                    answers.find((a) => a.questionIndex === currentQuestion)
                      ?.answer === option
                  }
                  control={
                    <RadioGroupItem
                      value={option}
                      className="shrink-0 border-input text-brand data-[state=checked]:border-brand"
                    />
                  }
                >
                  {option}
                </QuestionChoice>
              ))}
            </RadioGroup>
          </div>
        );
      case "text":
        return (
          <div>
            <QuestionTextField
              value={getTextAnswer(currentQuestion)}
              onChange={(value) => handleAnswer(currentQuestion, value)}
              placeholder="Upiši odgovor ovde"
              ariaLabelledBy={`quiz-question-${currentQuestion}`}
            />
          </div>
        );
      case "textarea":
        return (
          <div>
            <QuestionTextField
              value={getTextAnswer(currentQuestion)}
              onChange={(value) => handleAnswer(currentQuestion, value)}
              placeholder="Upiši odgovor ovde"
              ariaLabelledBy={`quiz-question-${currentQuestion}`}
              multiline
            />
          </div>
        );
      default:
        return null;
    }
  };

  const currentAnswer = answers.find(
    (answer) => answer.questionIndex === currentQuestion,
  )?.answer;
  const currentQuestionHeadingId = isOnEmailStep
    ? "quiz-email-heading"
    : `quiz-question-${currentQuestion}`;
  const isNextDisabled =
    !currentAnswer ||
    (Array.isArray(currentAnswer) && currentAnswer.length === 0);

  return (
    <main className="min-h-[calc(100dvh-4rem)] bg-background">
      <QuestionFlowShell
        maxWidthClassName="max-w-4xl"
        context={title}
        progressLabel={
          isOnEmailStep ? (
            "Poslednji korak"
          ) : (
            <>
              Pitanje {currentQuestion + 1} od {questions.length}
            </>
          )
        }
        progressAriaLabel={
          isOnEmailStep
            ? `Napredak, korak ${progressStep} od ${progressSteps}, email (opciono)`
            : `Napredak, pitanje ${progressStep} od ${progressSteps}`
        }
        progressMax={progressSteps}
        progressValue={progressStep}
      >
        <div className="pt-8 sm:pt-10">
          {currentQuestion < questions.length ? (
            <div>
              <h1
                id={currentQuestionHeadingId}
                className="max-w-2xl text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl"
              >
                {questions[currentQuestion]?.question}
              </h1>
              <div className="mt-8">
                {renderQuestion(questions[currentQuestion]!)}
              </div>
            </div>
          ) : !session?.user?.email ? (
            <div className="max-w-xl">
              <h1
                id={currentQuestionHeadingId}
                className="max-w-2xl text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl"
              >
                Tvoj rezultat je spreman!
              </h1>
              <p className="mt-4 text-base leading-7 text-muted-foreground">
                Email nije obavezan. Unesi ga samo ako želiš da ti rezultat
                stigne i na email.
              </p>
              <div className="mt-6">
                <OptionalEmailFields
                  id="quiz-email"
                  email={email}
                  emailError={emailError}
                  newsletterOptIn={newsletterOptIn}
                  onEmailChange={(value) => {
                    setEmail(value);
                    setEmailError(null);
                  }}
                  onNewsletterOptInChange={setNewsletterOptIn}
                />
              </div>
              <div className="mt-6">
                <Button
                  onClick={handleSubmit}
                  variant="cta"
                  size="lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? "Otvaramo rezultat..."
                    : email.trim().length > 0
                      ? "Pošalji i pogledaj rezultate!"
                      : "Pogledaj rezultate!"}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                {submitError && (
                  <p className="mt-3 text-sm text-destructive" role="alert">
                    {submitError}
                  </p>
                )}
              </div>
            </div>
          ) : null}
        </div>

        {currentQuestion < questions.length && (
          <div className="mt-10 flex items-center gap-3 border-t border-border pt-5">
            {currentQuestion > 0 && (
              <Button
                onClick={handlePrevious}
                variant="ghost"
                size="lg"
                className="mr-auto"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Prethodno
              </Button>
            )}
            <Button
              onClick={handleNext}
              variant="cta"
              size="lg"
              disabled={isNextDisabled || isSubmitting}
              className={cn(currentQuestion === 0 && "ml-auto")}
            >
              {isSubmitting ? "Otvaramo rezultat..." : "Nastavi"}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        )}

        {status !== "authenticated" && currentQuestion === 0 && (
          <p className="mt-10 max-w-2xl border-t border-border pt-5 text-xs leading-5 text-muted-foreground">
            Test koristi veštačku inteligenciju za personalizovan rezultat.
            Čuvamo tvoje odgovore i rezultat da se kasnije vratiš kontekstu,
            pratiš promene i podeliš rezultat sa terapeutom ako želiš.{" "}
            <Link
              href="/podrska/klijenti/psiholoski-testovi-i-ai-analiza"
              className="underline underline-offset-2 hover:text-foreground"
            >
              Saznaj više
            </Link>
          </p>
        )}
      </QuestionFlowShell>
    </main>
  );
}
